import { Fragment, useEffect, useState } from 'react'
import { useAuth } from 'react-oidc-context'
import { Alert, Button, DialogActions, DialogContent, DialogContentText, Divider, Grid, Typography } from '@mui/material'
import { DateTimeRangePicker, MultiInputDateTimeRangeField, renderDigitalClockTimeView } from '@mui/x-date-pickers-pro'
import { addMonths, intervalToDuration } from 'date-fns'
import { Form, Formik } from 'formik'
import { useSnackbar } from 'notistack'
import * as Yup from 'yup'

import { useHost } from '../../providers/app-provider'
import { DeviceTableRow } from '../../routes/accounts/components/devices-tab-panel/types'
import Api from '../../utils/api'
import { getPausedState } from '../../utils/hooks/usePauseDevice'
import DialogWithTitle from '../dialog-with-title'

import DeviceRow from './device-row'
import useStyles from './index.styles'

interface DevicePauseModalProps {
  devices: DeviceTableRow[] | IDeviceAPIObject[]
  open: boolean
  onClose: () => void
  onContinue: () => void
}

interface FormValues {
  startDate: Date | null
  endDate: Date | null
}

const schema = Yup.object().shape({
  endDate: Yup.date().required('Please select an end date.').min(Yup.ref('startDate'), 'End date must be after start date.'),
})

const calculateDuration = (startDate: Date, endDate: Date) => {
  const { years, months, days, hours, minutes } = intervalToDuration({ start: startDate, end: endDate })
  return `${years ? `${years} year${years > 1 ? 's' : ''} ` : ''}${months ? `${months} month${months > 1 ? 's' : ''} ` : ''}${days ? `${days} day${days > 1 ? 's' : ''} ` : ''}${
    hours ? `${hours} hour${hours > 1 ? 's' : ''} ` : ''
  }${minutes ? `${minutes} minute${minutes > 1 ? 's' : ''} ` : ''}`
}

const DevicePauseModal = ({ devices, open, onClose, onContinue }: DevicePauseModalProps) => {
  const classes = useStyles
  const { enqueueSnackbar } = useSnackbar()
  const auth = useAuth()
  const host = useHost()

  const hasPaused = !!devices.find((d) => {
    const { isPaused, willBePaused } = getPausedState(d)
    return isPaused || willBePaused
  })
  const [showModal, setShowModal] = useState<'override' | 'form'>()

  useEffect(() => {
    if (open && hasPaused) setShowModal('override')
    else if (open && !hasPaused) setShowModal('form')
  }, [hasPaused, open])

  const closeModal = () => {
    setShowModal(undefined)
    onClose()
  }

  const initialValues: FormValues = {
    startDate: new Date(),
    endDate: null,
  }

  const onSubmit = async ({ startDate, endDate }: FormValues) => {
    if (!host || !auth.user?.access_token) return
    const res = await Api.post(`${host}/api/v2/nodes/reactions/pause`, {
      deviceIds: devices.map((d) => d.id),
      startTime: startDate ? startDate.toISOString() : null,
      endTime: endDate ? endDate.toISOString() : null,
    })
    if (res.ok) {
      onContinue()
      closeModal()
      enqueueSnackbar(`Device${devices.length > 1 ? 's' : ''} paused successfully.`)
    } else {
      enqueueSnackbar(`Could not pause device${devices.length > 1 ? 's' : ''}.`, { variant: 'error' })
    }
  }

  return (
    <>
      <DialogWithTitle open={open && showModal === 'override'} title="Override existing schedule?" setOpen={closeModal}>
        <DialogContent>
          <DialogContentText>
            One or more selected devices have a pause scheduled. Scheduling a new pause will override any existing schedules.
          </DialogContentText>
          <DialogActions sx={{ marginTop: 2, marginBottom: 2 }}>
            <Button variant="outlined" onClick={closeModal}>
              Cancel
            </Button>
            <Button variant="contained" onClick={() => setShowModal('form')}>
              Confirm
            </Button>
          </DialogActions>
        </DialogContent>
      </DialogWithTitle>
      <DialogWithTitle open={open && showModal === 'form'} title="Pause" setOpen={closeModal}>
        <DialogContent>
          <Formik initialValues={{ startDate: initialValues.startDate, endDate: initialValues.endDate }} onSubmit={onSubmit} validationSchema={schema}>
            {({ isSubmitting, isValid, setFieldValue, values, dirty }) => (
              <Form>
                <Grid container={true} spacing={2}>
                  <Grid size={12}>
                    <DateTimeRangePicker
                      format="MM/dd/yyyy hh:mm a"
                      slots={{ field: MultiInputDateTimeRangeField }}
                      localeText={{ start: 'Start Date & Time', end: 'End Date & Time' }}
                      views={['day', 'hours']}
                      timeSteps={{ minutes: 5 }}
                      skipDisabled={true}
                      viewRenderers={{ hours: renderDigitalClockTimeView }}
                      desktopModeMediaQuery="@media (pointer: fine) and (min-height: 805px)"
                      disablePast
                      shouldDisableDate={(date, position) => {
                        if (position === 'start') return false
                        return values.startDate ? date > addMonths(new Date(values.startDate), 4) : false
                      }}
                      onChange={(value) => {
                        setFieldValue('startDate', value[0])
                        setFieldValue('endDate', value[1], true)
                      }}
                    />
                    {values.endDate && isValid && (
                      <Alert severity="success" icon={false} sx={{ marginTop: 1 }}>
                        Pausing for {calculateDuration(values.startDate || new Date(), values.endDate)}
                      </Alert>
                    )}
                  </Grid>
                  <Grid
                    container={true}
                    size={12}
                    sx={{
                      ml: 1,
                    }}
                  >
                    <Grid size={12}>
                      <Typography variant="subtitle2">Pause Item{devices.length > 1 && <>s ({devices.length})</>}</Typography>
                      <Divider />
                    </Grid>

                    <Grid sx={classes.itemsList} size={12}>
                      {hasPaused && (
                        <Alert severity="warning" icon={false}>
                          Changes will override previously scheduled pauses
                        </Alert>
                      )}
                      {devices.map((device) => (
                        <Fragment key={device.id}>
                          <DeviceRow device={device} />
                        </Fragment>
                      ))}
                    </Grid>
                  </Grid>

                  <Grid
                    container={true}
                    size={12}
                    sx={{
                      justifyContent: 'flex-end',
                      gap: 2,
                      mt: 1,
                    }}
                  >
                    <Button variant="outlined" onClick={closeModal}>
                      Discard Changes
                    </Button>
                    <Button type="submit" variant="contained" disabled={!devices.length || isSubmitting || !isValid || !dirty}>
                      {hasPaused ? 'Update' : 'Schedule'}
                    </Button>
                  </Grid>
                </Grid>
              </Form>
            )}
          </Formik>
        </DialogContent>
      </DialogWithTitle>
    </>
  )
}

export default DevicePauseModal
